// The assistant answers in markdown — "## This Week's Meals", "- **Ava
// Libero** — Peanuts" — and the chat showed it raw, asterisks and all
// (2026-09-21). This parses the small subset it actually writes into a plain
// tree the chat renders as React elements: nothing here touches HTML, so
// nothing in a reply can inject any.
//
// Replies stream in a token at a time, so half of a marker is a normal state:
// an unclosed "**" or "[label](" is left as the text it is, and turns into
// formatting on the delta that closes it.

const HEADING = /^(#{1,6})\s+(.+?)\s*#*\s*$/;
const BULLET = /^(\s*)[-*•]\s+(.+)$/;
const ORDERED = /^(\s*)(\d{1,3})[.)]\s+(.+)$/;
const RULE = /^\s*([-*_])(?:\s*\1){2,}\s*$/;
const FENCE = /^\s*```/;
// "> Subject: Duplicate charge…": the support email the assistant drafts for
// the coach to approve. It showed a literal ">" on every line.
const QUOTE = /^\s*>\s?(.*)$/;

// **bold**, *italic*, `code`, [label](target), and a bare http(s) URL (the
// assistant's share links arrive bare; trailing sentence punctuation is not
// part of the link). The text inside a pair of asterisks must start and end on
// a non-space, so "2 * 3 * 4" and a stray "*" stay text. A link target, and a
// bare URL, may hold one level of balanced parentheses
// (".../wiki/Mercury_(planet)"): the first ")" used to end it. (Written
// without lookbehind: older Safari cannot parse one, and this file loads with
// the header on every page.)
const URL_CHAR = "[^\\s<>()[\\]*`]";
const URL_END = "[^\\s<>()[\\]*`.,;:!?'\"]";
const URL_PARENS = `\\(${URL_CHAR}*\\)`;
const INLINE = new RegExp(
  [
    '\\*\\*([^\\s*](?:[^*\\n]*?[^\\s*])?)\\*\\*',
    '\\*([^\\s*](?:[^*\\n]*?[^\\s*])?)\\*',
    '`([^`\\n]+)`',
    '\\[([^\\]\\n]+)\\]\\(((?:[^()\\s]|\\([^()\\s]*\\))+)\\)',
    `(https?:\\/\\/(?:${URL_CHAR}|${URL_PARENS})*(?:${URL_END}|${URL_PARENS}))`,
  ].join('|'),
  'g',
);
const DIGIT = /\d/;

// Where a link may go: another site, or a page of this app. Anything else
// (javascript:, data:, a bare word) is shown as its label, not as a link.
export const classifyLinkTarget = (target) => {
  const value = String(target ?? '').trim();
  if (/^https?:\/\//i.test(value)) return { kind: 'external', href: value };
  if (/^\/(?!\/)/.test(value)) return { kind: 'internal', path: value };
  return null;
};

export function parseInline(text) {
  const source = String(text ?? '');
  const nodes = [];
  let last = 0;
  // A pattern of its own per call: the recursion below re-enters this
  // function, and a shared lastIndex would send the outer walk back to zero.
  const pattern = new RegExp(INLINE.source, 'g');
  for (let match = pattern.exec(source); match; match = pattern.exec(source)) {
    const [whole, bold, italic, code, label, target, url] = match;
    // "5*6*7" is arithmetic, not "5, italic 6, 7": an asterisk between two
    // digits opens nothing. Its text stays put and the scan goes on after it.
    if (italic != null && DIGIT.test(source[match.index - 1] ?? '') && DIGIT.test(italic[0])) {
      pattern.lastIndex = match.index + 1;
      continue;
    }
    if (match.index > last) nodes.push({ type: 'text', text: source.slice(last, match.index) });
    if (bold != null) nodes.push({ type: 'bold', children: parseInline(bold) });
    else if (italic != null) nodes.push({ type: 'italic', children: parseInline(italic) });
    else if (code != null) nodes.push({ type: 'code', text: code });
    else if (url != null) nodes.push({ type: 'link', kind: 'external', href: url, children: [{ type: 'text', text: url }] });
    else {
      const link = classifyLinkTarget(target);
      if (link) nodes.push({ type: 'link', ...link, children: parseInline(label) });
      else nodes.push({ type: 'text', text: label });
    }
    last = match.index + whole.length;
  }
  if (last < source.length) nodes.push({ type: 'text', text: source.slice(last) });
  return nodes;
}

const indentLevel = (spaces) => Math.min(2, Math.floor(String(spaces || '').replace(/\t/g, '  ').length / 2));

// GFM tables ("| Name | Role |" over "|---|---|"), which the assistant writes
// for a roster with allergies. Without them a table showed as one paragraph
// of pipes (prod QA 2026-10-06). A table starts at a row followed by a
// delimiter row with as many cells, and runs until a blank line or a line
// with no pipe. "\|" is a pipe inside a cell. While the delimiter row is still
// streaming in, the header row shows as text, as half-arrived markup does.
const UNESCAPED_PIPE = /(^|[^\\])\|/;
const DELIMITER_CELL = /^:?-+:?$/;

const splitTableRow = (line) => {
  let text = line.trim();
  if (text.startsWith('|')) text = text.slice(1);
  if (text.endsWith('|') && !text.endsWith('\\|')) text = text.slice(0, -1);
  const cells = [];
  let cell = '';
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === '\\' && text[index + 1] === '|') {
      cell += '|';
      index += 1;
    } else if (char === '|') {
      cells.push(cell.trim());
      cell = '';
    } else {
      cell += char;
    }
  }
  cells.push(cell.trim());
  return cells;
};

const tableAlignments = (line) => {
  if (!UNESCAPED_PIPE.test(line)) return null;
  const cells = splitTableRow(line);
  if (!cells.every((cell) => DELIMITER_CELL.test(cell))) return null;
  return cells.map((cell) => {
    const left = cell.startsWith(':');
    const right = cell.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    if (left) return 'left';
    return null;
  });
};

const tableCells = (line, width) =>
  Array.from({ length: width }, (_, index) => parseInline(splitTableRow(line)[index] ?? ''));

export function parseAssistantMarkdown(text) {
  const lines = String(text ?? '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let paragraph = null;
  let list = null;
  let fence = null;

  const closeParagraph = () => { paragraph = null; };
  const closeList = () => { list = null; };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    if (fence) {
      if (FENCE.test(line)) fence = null;
      else fence.lines.push(line);
      continue;
    }
    if (FENCE.test(line)) {
      closeParagraph();
      closeList();
      fence = { type: 'codeblock', lines: [] };
      blocks.push(fence);
      continue;
    }
    if (!line.trim()) {
      closeParagraph();
      closeList();
      continue;
    }
    // A run of "> " lines is one quote, its inside parsed like a reply of its
    // own (a bare ">" is a paragraph break inside it).
    if (QUOTE.test(line)) {
      closeParagraph();
      closeList();
      const quoted = [];
      while (index < lines.length && QUOTE.test(lines[index])) {
        quoted.push(QUOTE.exec(lines[index])[1]);
        index += 1;
      }
      index -= 1;
      blocks.push({ type: 'blockquote', blocks: parseAssistantMarkdown(quoted.join('\n')) });
      continue;
    }
    const align = UNESCAPED_PIPE.test(line) && index + 1 < lines.length ? tableAlignments(lines[index + 1]) : null;
    if (align && align.length === splitTableRow(line).length) {
      closeParagraph();
      closeList();
      const table = { type: 'table', align, header: tableCells(line, align.length), rows: [] };
      blocks.push(table);
      index += 1;
      while (index + 1 < lines.length && lines[index + 1].trim() && UNESCAPED_PIPE.test(lines[index + 1])) {
        index += 1;
        table.rows.push(tableCells(lines[index], align.length));
      }
      continue;
    }
    if (RULE.test(line)) {
      closeParagraph();
      closeList();
      blocks.push({ type: 'rule' });
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      closeParagraph();
      closeList();
      blocks.push({ type: 'heading', level: heading[1].length, children: parseInline(heading[2]) });
      continue;
    }
    const bullet = BULLET.exec(line);
    const ordered = bullet ? null : ORDERED.exec(line);
    if (bullet || ordered) {
      closeParagraph();
      const kind = bullet ? 'bullet' : 'ordered';
      if (!list || list.kind !== kind) {
        list = { type: 'list', kind, items: [] };
        blocks.push(list);
      }
      list.items.push(
        bullet
          ? { indent: indentLevel(bullet[1]), children: parseInline(bullet[2]) }
          : { indent: indentLevel(ordered[1]), number: Number(ordered[2]), children: parseInline(ordered[3]) },
      );
      continue;
    }
    closeList();
    if (!paragraph) {
      paragraph = { type: 'paragraph', lines: [] };
      blocks.push(paragraph);
    }
    paragraph.lines.push(parseInline(line));
  }

  return blocks;
}
